import eventBus from './globalEventBus';
import apiClient from './apiClient';
import cacheableRequest from './cacheableRequest';
import submitCommand from './submitCommand';
import cacheService from './cacheService';
import uuid from './uuid';
import { logout, persistStoredUser, transformSessionUser } from '../features/authentication';
import { keepJwt, validateJwt } from './jwt';

const userService = {
  authorizeV1: (code, host) => apiClient.post('/v1/authorize', { code, host }),

  authorizeV2: (token, host) => apiClient.post('/v2/authorize', { token: token, host: host }),

  getUserOrgs: () =>
    apiClient
      .get('/v2/user/organizations')
      .then(response => response.data)
      .catch(reason => reason),

  switchOrg: (userId, orgId) =>
    submitCommand(userId, { id: userId, accountId: orgId }, 'SelectAccount', 'AccountSelected'),

  getProfile: userId => cacheableRequest(`/v1/users/${userId}`).then(response => response.data),
  savePreferences: (userId, showAllInfoBoxes) => {
    cacheService.remove('/preferences');
    return apiClient.post('/preferences', { _id: { id: userId }, showAllInformationBoxes: showAllInfoBoxes });
  },

  impersonateUser: email => {
    return apiClient.post(`/v1/impersonate?email=${email}`).then(response => {
      if (response.status !== 200) {
        return Promise.reject('Invalid response');
      }

      if (response.headers && response.headers['set-authorization']) {
        const token = response.headers['set-authorization'];
        if (validateJwt(token)) keepJwt(token);
      }

      const transformedUser = transformSessionUser(response.data);

      persistStoredUser(transformedUser);
      eventBus.publish('UserLoggedIn', transformedUser);

      return transformedUser;
    });
  },

  getRedirectHashAfterLogin: user => {
    let hash = '/dashboard';

    if (user?.redirectURL?.includes('#')) {
      hash = user.redirectURL.split('#')[1];
    } else if (user?.redirectURL) {
      hash = user.redirectURL;
    } else if (localStorage.getItem('newHash')) {
      hash = localStorage.getItem('newHash');
    }

    return hash;
  },

  getPreferences: () => cacheableRequest('/preferences').then(response => response.data),

  setProfileImage: userId => {
    const key = `${userId}profile.png`;
    const cmd = { id: userId, imageUrl: key, initiatingUserId: userId };

    return submitCommand(userId, cmd, 'UpdateUserProfileImage', 'UserProfileImageUpdated');
  },

  sendFeedbackEmail: (userId, name, email, subject, message, orgName = '') => {
    const id = uuid.generate();
    const gitHash = getCommitHash();
    const cmd = {
      id,
      userId,
      name,
      email,
      subject,
      description: message,
      callbackHost: window.location.origin,
      orgName,
      browserInfo: `${navigator.userAgent} gitHash: ${gitHash}`,
    };
    return submitCommand(id, cmd, 'SendFeedbackEmail', 'FeedbackEmailSent', 'EmailError');
  },

  getUserIdByEmail: email =>
    cacheableRequest(`/users/id?email=${encodeURIComponent(email)}`).then(response => response.data),

  getUserById: userId => cacheableRequest(`/v1/users/${userId}`).then(response => response.data),

  getProfileImageUrlById: id => `${apiClient.getConfig().baseURL}/v1/images/profile/${id}profile.png`,

  getHasAdminPermission: () => apiClient.get('/v1/admins/permissions').then(response => !!response.data?.isAdmin),
  logout: () => logout(),
};

export default userService;
